
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../api';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Shield, ShieldAlert, ShieldCheck, Info, User, Package, Settings } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../ui/tooltip';
import { toast } from 'sonner';

const MembershipSection = ({ formData, setFormData, clientId }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [effectiveCaps, setEffectiveCaps] = useState([]);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/admin/membership-config`);
        setConfig(response.data);
      } catch (error) {
        console.error('Error fetching membership config:', error);
        toast.error('Error al cargar configuración de membresías');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    if (clientId) {
      // Fetch effective caps when something relevant changes or initially
      const fetchEffectiveCaps = async () => {
        setCalculating(true);
        try {
          // We could calculate this locally, but backend-side is source of truth
          // However, for immediate visual feedback during editing, we should send current state
          // For now, let's just fetch for existing user
          const response = await axios.get(`${API}/admin/membership-config/user-effective/${clientId}`);
          setEffectiveCaps(response.data.effective_capabilities);
        } catch (error) {
          console.error('Error fetching effective caps:', error);
        } finally {
          setCalculating(false);
        }
      };
      fetchEffectiveCaps();
    }
  }, [clientId, formData.rol_general, formData.membresia_id, formData.membresia_estado, formData.capabilities_extra, formData.capabilities_denied]);

  if (loading) return <div className="p-4 text-center animate-pulse text-slate-500">Cargando configuración de membresías...</div>;

  const toggleCapability = (field, capCode) => {
    const current = formData[field] || [];
    if (current.includes(capCode)) {
      setFormData({ ...formData, [field]: current.filter(c => c !== capCode) });
    } else {
      setFormData({ ...formData, [field]: [...current, capCode] });
    }
  };

  return (
    <div className="space-y-6 mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        <Shield className="w-5 h-5 text-indigo-600" />
        <h3 className="text-lg font-bold text-slate-800">Capa de Membresía y Accesos</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Layer 1: Rol General */}
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
            <User className="w-3 h-3" />
            1. Rol General
          </Label>
          <select
            value={formData.rol_general || 'cliente'}
            onChange={(e) => setFormData({ ...formData, rol_general: e.target.value })}
            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
          >
            {config?.available_roles.map(role => (
              <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ')}</option>
            ))}
          </select>
          <p className="text-[10px] text-slate-400 italic">Define la jerarquía básica del usuario en el ecosistema.</p>
        </div>

        {/* Layer 2: Membresía */}
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
            <Package className="w-3 h-3" />
            2. Membresía Principal
          </Label>
          <select
            value={formData.membresia_id || ''}
            onChange={(e) => setFormData({ ...formData, membresia_id: e.target.value })}
            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
          >
            <option value="">-- Ninguna --</option>
            {config?.memberships.map(m => (
              <option key={m.code} value={m.code}>{m.name}</option>
            ))}
          </select>
          <div className="flex items-center gap-2 mt-2">
            <Label className="text-[10px] text-slate-500">Estado:</Label>
            <select
              value={formData.membresia_estado || 'activa'}
              onChange={(e) => setFormData({ ...formData, membresia_estado: e.target.value })}
              className={`text-[10px] px-1 py-0.5 rounded border ${
                formData.membresia_estado === 'activa' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 
                formData.membresia_estado === 'pausada' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                'bg-slate-50 text-slate-700 border-slate-200'
              }`}
            >
              <option value="activa">ACTIVA</option>
              <option value="pausada">PAUSADA</option>
              <option value="cancelada">CANCELADA</option>
            </select>
          </div>
        </div>

        {/* Capacidad Efectiva (Read-Only) */}
        <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
          <Label className="text-xs font-bold text-indigo-700 flex items-center gap-1 mb-2">
            <ShieldCheck className="w-4 h-4" />
            Accesos Resultantes (Effective)
          </Label>
          <div className="flex flex-wrap gap-1">
            {effectiveCaps.length > 0 ? (
              effectiveCaps.map(cap => (
                <Badge key={cap} variant="secondary" className="bg-indigo-50 text-indigo-700 border-indigo-100 text-[10px] py-0">
                  {cap}
                </Badge>
              ))
            ) : (
              <span className="text-[10px] text-slate-400 italic">Sin accesos calculados</span>
            )}
            {calculating && <span className="text-[10px] text-indigo-400 animate-pulse ml-1">...</span>}
          </div>
        </div>
      </div>

      {/* Layer 3: Excepciones / Capabilities */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-200 pt-4">
        <div className="bg-emerald-50/50 p-4 rounded-lg border border-emerald-100">
          <Label className="text-xs font-bold text-emerald-700 flex items-center gap-1 mb-3">
            <ShieldAlert className="w-4 h-4" />
            3a. Capabilities EXTRA (Overrides +)
          </Label>
          <div className="space-y-2">
            {config?.available_capabilities.map(cap => (
              <div key={cap.code} className="flex items-center gap-2 group">
                <Checkbox 
                  id={`extra-${cap.code}`}
                  checked={(formData.capabilities_extra || []).includes(cap.code)}
                  onCheckedChange={() => toggleCapability('capabilities_extra', cap.code)}
                />
                <label 
                  htmlFor={`extra-${cap.code}`} 
                  className="text-xs text-slate-700 cursor-pointer group-hover:text-emerald-700 transition-colors"
                  title={cap.description}
                >
                  {cap.name} <span className="text-[10px] text-slate-400">({cap.code})</span>
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-rose-50/50 p-4 rounded-lg border border-rose-100">
          <Label className="text-xs font-bold text-rose-700 flex items-center gap-1 mb-3">
            <ShieldAlert className="w-4 h-4" />
            3b. Capabilities DENEGADAS (Overrides -)
          </Label>
          <div className="space-y-2">
            {config?.available_capabilities.map(cap => (
              <div key={cap.code} className="flex items-center gap-2 group">
                <Checkbox 
                  id={`denied-${cap.code}`}
                  checked={(formData.capabilities_denied || []).includes(cap.code)}
                  onCheckedChange={() => toggleCapability('capabilities_denied', cap.code)}
                />
                <label 
                  htmlFor={`denied-${cap.code}`} 
                  className="text-xs text-slate-700 cursor-pointer group-hover:text-rose-700 transition-colors"
                  title={cap.description}
                >
                  {cap.name} <span className="text-[10px] text-slate-400">({cap.code})</span>
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-2 p-3 bg-indigo-50 rounded-lg border border-indigo-100 mt-2">
        <Info className="w-4 h-4 text-indigo-600 flex-shrink-0" />
        <p className="text-[10px] text-indigo-800 leading-tight">
          <strong>Protocolo:</strong> Las capacidades denegadas tienen prioridad absoluta sobre las heredadas o extra. 
          El sistema recalculará el acceso al Canal MPD en tiempo real tras guardar.
        </p>
      </div>
    </div>
  );
};

export default MembershipSection;
